import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import * as Types from '@/types'
import { apiClient } from '@/services/api'

interface AnalyticsState {
  operationKPIs: {
    data: Types.OperationKPI | null
    operationId: string | null
    isLoading: boolean
    error: string | null
  }
  tenantKPIs: {
    data: Types.TenantKPI | null
    isLoading: boolean
    error: string | null
    lastUpdated: number | null
  }
  personnelPerformance: {
    data: any | null
    items: Types.PersonnelPerformance[]
    total: number
    page: number
    pageSize: number
    isLoading: boolean
    error: string | null
  }
  toolUtilization: {
    data: any | null
    items: Types.ToolUtilization[]
    total: number
    page: number
    pageSize: number
    isLoading: boolean
    error: string | null
  }
}

const initialState: AnalyticsState = {
  operationKPIs: {
    data: null,
    operationId: null,
    isLoading: false,
    error: null,
  },
  tenantKPIs: {
    data: null,
    isLoading: false,
    error: null,
    lastUpdated: null,
  },
  personnelPerformance: {
    data: null,
    items: [],
    total: 0,
    page: 1,
    pageSize: 20,
    isLoading: false,
    error: null,
  },
  toolUtilization: {
    data: null,
    items: [],
    total: 0,
    page: 1,
    pageSize: 20,
    isLoading: false,
    error: null,
  },
}

// Operation KPIs async thunk
export const fetchOperationKPIs = createAsyncThunk(
  'analytics/fetchOperationKPIs',
  async (operationId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.getOperationKPIs(operationId)
      return { data: response, operationId }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch operation KPIs')
    }
  }
)

// Tenant KPIs async thunk
export const fetchTenantKPIs = createAsyncThunk(
  'analytics/fetchTenantKPIs',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.getTenantKPIs()
      return { data: response, timestamp: Date.now() }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch tenant KPIs')
    }
  }
)

// Personnel Performance async thunk
export const fetchPersonnelPerformance = createAsyncThunk(
  'analytics/fetchPersonnelPerformance',
  async (
    { page, limit }: { page?: number; limit?: number } = {},
    { rejectWithValue }
  ) => {
    try {
      const skip = page ? (page - 1) * (limit || 20) : 0
      const response = await apiClient.getPersonnelPerformance({
        skip,
        limit,
      })
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch personnel performance')
    }
  }
)

// Tool Utilization async thunk
export const fetchToolUtilization = createAsyncThunk(
  'analytics/fetchToolUtilization',
  async (
    { page, limit }: { page?: number; limit?: number } = {},
    { rejectWithValue }
  ) => {
    try {
      const skip = page ? (page - 1) * (limit || 20) : 0
      const response = await apiClient.getToolUtilization({
        skip,
        limit,
      })
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch tool utilization')
    }
  }
)

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    clearError: (state) => {
      state.operationKPIs.error = null
      state.tenantKPIs.error = null
      state.personnelPerformance.error = null
      state.toolUtilization.error = null
    },
    resetOperationKPIs: (state) => {
      state.operationKPIs.data = null
      state.operationKPIs.operationId = null
    },
  },
  extraReducers: (builder) => {
    // Operation KPIs
    builder
      .addCase(fetchOperationKPIs.pending, (state) => {
        state.operationKPIs.isLoading = true
        state.operationKPIs.error = null
      })
      .addCase(fetchOperationKPIs.fulfilled, (state, action) => {
        state.operationKPIs.isLoading = false
        state.operationKPIs.data = action.payload.data
        state.operationKPIs.operationId = action.payload.operationId
      })
      .addCase(fetchOperationKPIs.rejected, (state, action) => {
        state.operationKPIs.isLoading = false
        state.operationKPIs.error = action.payload as string
      })

    // Tenant KPIs
    builder
      .addCase(fetchTenantKPIs.pending, (state) => {
        state.tenantKPIs.isLoading = true
        state.tenantKPIs.error = null
      })
      .addCase(fetchTenantKPIs.fulfilled, (state, action) => {
        state.tenantKPIs.isLoading = false
        state.tenantKPIs.data = action.payload.data
        state.tenantKPIs.lastUpdated = action.payload.timestamp
      })
      .addCase(fetchTenantKPIs.rejected, (state, action) => {
        state.tenantKPIs.isLoading = false
        state.tenantKPIs.error = action.payload as string
      })

    // Personnel Performance
    builder
      .addCase(fetchPersonnelPerformance.pending, (state) => {
        state.personnelPerformance.isLoading = true
        state.personnelPerformance.error = null
      })
      .addCase(fetchPersonnelPerformance.fulfilled, (state, action) => {
        state.personnelPerformance.isLoading = false
        state.personnelPerformance.data = action.payload
        state.personnelPerformance.items = action.payload.items || []
        state.personnelPerformance.total = action.payload.total || 0
        state.personnelPerformance.page = action.payload.page || 1
        state.personnelPerformance.pageSize = action.payload.page_size || 20
      })
      .addCase(fetchPersonnelPerformance.rejected, (state, action) => {
        state.personnelPerformance.isLoading = false
        state.personnelPerformance.error = action.payload as string
      })

    // Tool Utilization
    builder
      .addCase(fetchToolUtilization.pending, (state) => {
        state.toolUtilization.isLoading = true
        state.toolUtilization.error = null
      })
      .addCase(fetchToolUtilization.fulfilled, (state, action) => {
        state.toolUtilization.isLoading = false
        state.toolUtilization.data = action.payload
        state.toolUtilization.items = action.payload.items || []
        state.toolUtilization.total = action.payload.total || 0
        state.toolUtilization.page = action.payload.page || 1
        state.toolUtilization.pageSize = action.payload.page_size || 20
      })
      .addCase(fetchToolUtilization.rejected, (state, action) => {
        state.toolUtilization.isLoading = false
        state.toolUtilization.error = action.payload as string
      })
  },
})

export const { clearError, resetOperationKPIs } = analyticsSlice.actions
export default analyticsSlice.reducer
